import type { CoviChatItem } from "../model/types"
import { parseAudioAnalysis } from "./audioAnalyses"
import {
  parseProposedActions,
  parseStoredLegacyMetricProposal,
} from "./proposedActions"
import { parseToolSteps, toolState } from "./toolSteps"
import { boundTranscriptToolSteps } from "./toolTraceBounds"

export function isStorageRecord(
  value: unknown,
): value is Record<string, unknown> {
  return value != null && typeof value === "object"
}

function isValidActionStatus(value: unknown): boolean {
  return (
    value === "proposed" ||
    value === "executing" ||
    value === "done" ||
    value === "error" ||
    value === "dismissed"
  )
}

function isValidChatItem(item: unknown): item is CoviChatItem {
  if (!isStorageRecord(item) || typeof item.id !== "string") {
    return false
  }

  switch (item.kind) {
    case "analysis":
      return (
        parseAudioAnalysis(item.analysis) !== null &&
        typeof item.firstSeenAt === "number" &&
        Number.isFinite(item.firstSeenAt)
      )
    case "assistant":
      return (
        typeof item.text === "string" &&
        (item.confirmationReceiptFor === undefined ||
          typeof item.confirmationReceiptFor === "string") &&
        (item.notebookConversionReceipt === undefined ||
          typeof item.notebookConversionReceipt === "boolean")
      )
    case "user":
    case "error":
      return typeof item.text === "string"
    case "tool":
      return (
        typeof item.name === "string" &&
        typeof item.summary === "string" &&
        typeof item.running === "boolean" &&
        typeof item.isError === "boolean"
      )
    case "action":
      return (
        (parseProposedActions([item.proposal]) !== null ||
          parseStoredLegacyMetricProposal(item.proposal) !== null) &&
        typeof item.proposedAt === "number" &&
        Number.isFinite(item.proposedAt) &&
        isValidActionStatus(item.status) &&
        (item.proposalBatchId === undefined ||
          (typeof item.proposalBatchId === "string" &&
            item.proposalBatchId.length > 0)) &&
        (item.detail === undefined || typeof item.detail === "string") &&
        (item.href === undefined || typeof item.href === "string") &&
        (item.error === undefined || typeof item.error === "string") &&
        (item.confirmationParentId === undefined ||
          typeof item.confirmationParentId === "string") &&
        (item.feedback === undefined ||
          (isStorageRecord(item.feedback) &&
            (item.feedback.text === undefined ||
              typeof item.feedback.text === "string") &&
            (item.feedback.warning === undefined ||
              typeof item.feedback.warning === "string") &&
            (item.feedback.steps === undefined ||
              (Array.isArray(item.feedback.steps) &&
                (item.feedback.steps.length === 0 ||
                  parseToolSteps(item.feedback.steps)?.length ===
                    item.feedback.steps.length)))))
      )
    default:
      return false
  }
}

export function sanitizeHydratedItems(items: unknown[]): CoviChatItem[] {
  const sanitized: CoviChatItem[] = []
  for (const item of items) {
    if (!isValidChatItem(item)) {
      continue
    }
    if (item.kind === "analysis") {
      const analysis = parseAudioAnalysis(item.analysis)
      if (analysis) {
        sanitized.push({
          kind: "analysis",
          id: item.id,
          analysis,
          firstSeenAt: item.firstSeenAt,
        })
      }
      continue
    }
    if (item.kind === "tool") {
      sanitized.push({
        ...item,
        status: typeof item.status === "string" ? item.status : "unknown",
        isError: toolState(item) === "failed",
        running: toolState(item) === "running",
      })
      continue
    }
    if (item.kind !== "action") {
      sanitized.push(item)
      continue
    }
    if (item.status === "executing") {
      sanitized.push({
        ...item,
        status: "error",
        settledAt: Date.now(),
        error:
          "Interrupted — the panel reloaded while this action was running.",
      })
      continue
    }
    sanitized.push(item)
  }
  return boundTranscriptToolSteps(sanitized)
}
